import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, from, map, Observable, of, retry, switchMap } from 'rxjs';

import { OrganisationSuiteService } from '../../services/organisation-suite.service';
import { handleHttpError } from '../../utils/api';
import { OrganisationSuiteActions } from '../actions/organisation-suite.actions';

@Injectable()
export class OrganisationSuiteEffects {
  public getAll$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.getAll),
      switchMap(({ organisationId }) =>
        from(this.service.getAll(organisationId)).pipe(
          map(({ items }) => OrganisationSuiteActions.getAllSuccess({ suites: items })),
          catchError((error) => of(OrganisationSuiteActions.getAllFailure({ error }))),
        ),
      ),
    ),
  );

  public getAllFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.getAllFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.get),
      switchMap(({ organisationId, code }) =>
        from(this.service.getOne(organisationId, code)).pipe(
          map((suite) => OrganisationSuiteActions.getSuccess({ suite })),
          catchError((error) => of(OrganisationSuiteActions.getFailure({ error }))),
        ),
      ),
    ),
  );

  public getFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.getFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.create),
      switchMap(({ organisationId, payload }) =>
        from(this.service.create(organisationId, payload)).pipe(
          retry(2),
          map((suite) => OrganisationSuiteActions.createSuccess({ suite })),
          catchError((error) => of(OrganisationSuiteActions.createFailure({ error }))),
        ),
      ),
    ),
  );

  public createFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.createFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.update),
      switchMap(({ organisationId, code, payload }) =>
        from(this.service.update(organisationId, code, payload)).pipe(
          map((suite) => OrganisationSuiteActions.updateSuccess({ suite })),
          catchError((error) => of(OrganisationSuiteActions.updateFailure({ error }))),
        ),
      ),
    ),
  );

  public updateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganisationSuiteActions.updateFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public constructor(
    private readonly actions$: Actions,
    private readonly service: OrganisationSuiteService,
  ) {}
}
